import { useParsedStorage } from '../../../../core/hooks/useParsedStorage'
import { useCurrentUser } from '../../../utils/currentUserContext'
import type { UserCapability, EnumValueMeta } from '@joyful/utils'
import { createEnumSchema } from '@joyful/utils'

export type EncounterSidebarDefaultView =
  | 'action'
  | 'agent'
  | 'agent-latest-chat'
  | 'timeline'

interface EncounterSidebarDefaultViewMeta extends EnumValueMeta {
  capability?: UserCapability
}

export const EncounterSidebarDefaultViewSchema = createEnumSchema<
  EncounterSidebarDefaultView,
  EncounterSidebarDefaultViewMeta
>([
  {
    description: 'Open the timeline tab',
    title: 'Timeline',
    value: 'timeline',
  },
  {
    capability: 'agent',
    description: 'Open the agent tab',
    title: 'Agent',
    value: 'agent',
  },
  {
    capability: 'agent',
    description: 'Open the agent tab and select the latest chat',
    title: 'Agent (latest chat)',
    value: 'agent-latest-chat',
  },
  {
    capability: 'encounter.action_perform',
    description: 'Open the action tab',
    title: 'Action',
    value: 'action',
  },
])

export function getEncounterSidebarDefaultViewOptions({
  hasCapability,
  isRedactionActive,
}: {
  hasCapability: (capability: UserCapability) => boolean
  isRedactionActive: boolean
}) {
  return EncounterSidebarDefaultViewSchema.definitions.flatMap((definition) => {
    if (isRedactionActive && definition.value === 'action') {
      return []
    }

    if (
      definition.capability &&
      hasCapability(definition.capability) === false
    ) {
      return []
    }

    return [definition]
  })
}

export function getEncounterSidebarTabForDefaultView(
  defaultView: EncounterSidebarDefaultView,
): Exclude<EncounterSidebarDefaultView, 'agent-latest-chat'> {
  if (defaultView === 'agent-latest-chat') {
    return 'agent'
  }

  return defaultView
}

export function resolveEncounterSidebarDefaultView({
  availableDefaultViews,
  storedDefaultView,
}: {
  availableDefaultViews: ReadonlyArray<EncounterSidebarDefaultView>
  storedDefaultView: EncounterSidebarDefaultView
}): EncounterSidebarDefaultView {
  if (availableDefaultViews.includes(storedDefaultView)) {
    return storedDefaultView
  }

  return availableDefaultViews.at(0) ?? 'timeline'
}

export function isAgentLatestChatDefaultView(
  defaultView: EncounterSidebarDefaultView,
): boolean {
  return defaultView === 'agent-latest-chat'
}

export function useEncounterSidebarDefaultView() {
  const currentUser = useCurrentUser()
  const [storedDefaultView, setStoredDefaultView] = useParsedStorage(
    `encounter-sidebar-default-view:${currentUser.id}`,
    {
      initialValue: 'timeline',
      schema: EncounterSidebarDefaultViewSchema.schema,
      strategy: 'local',
    },
  )

  return {
    setStoredDefaultView,
    storedDefaultView,
  }
}
